import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { ActivityInput } from './types';

export const activityKeys = {
  all: ['crm-activities'] as const,
  lists: () => [...activityKeys.all, 'list'] as const,
  list: (p: api.ActivityListParams) => [...activityKeys.lists(), p] as const,
};

export function useActivities(params: api.ActivityListParams = {}) {
  return useQuery({
    queryKey: activityKeys.list(params),
    queryFn: () => api.listActivities(params),
    placeholderData: keepPreviousData,
  });
}

export function useCreateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ActivityInput) => api.createActivity(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: activityKeys.lists() });
      toast.success('Activity logged');
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useUpdateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<ActivityInput> }) =>
      api.updateActivity(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: activityKeys.lists() });
    },
    onError: (err: ApiError) => {
      if (!err.fieldErrors) toast.error(err.message);
    },
  });
}

export function useArchiveActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.archiveActivity(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: activityKeys.lists() });
      toast.success('Activity archived');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function useRestoreActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.restoreActivity(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: activityKeys.lists() });
      toast.success('Activity restored');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}

export function usePurgeActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.purgeActivity(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: activityKeys.lists() });
      toast.success('Activity permanently deleted');
    },
    onError: (err: ApiError) => toast.error(err.message),
  });
}
